import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Search,
  MapPin,
  Star,
  Clock,
  ShoppingBag,
  User,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  Filter,
} from 'lucide-react';

import { useLocation, useNavigate } from 'react-router-dom';
import { resolveStoreCardImage, DEFAULT_BANNER } from '@/lib/storeImages';
import { OFFLINE_CATEGORIES, matchRestaurantCategory, normalizeCategoriesPayload, normalizeCategoryName } from '@/lib/categories';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import '../App.css';
import { backendGet } from '../lib/backendClient';

const HOME_OFFLINE_MOCKS_ENABLED = process.env.REACT_APP_HOME_OFFLINE_MOCKS === 'true';
const DEFAULT_BANNER_CTA = {
  enabled: true,
  text: 'Ver ofertas',
  link: '/promocoes',
};

const normalizeSearchToken = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .trim();

const buildRestaurantSearchText = (restaurant) => {
  const categoriesRaw = restaurant?.categories ?? restaurant?.category;
  const categoriesText = Array.isArray(categoriesRaw)
    ? categoriesRaw.map((item) => String(item || '')).join(' ')
    : String(categoriesRaw || '');

  return [
    restaurant?.name,
    restaurant?.store_name,
    restaurant?.nome,
    restaurant?.description,
    restaurant?.descricao,
    categoriesText,
    ...(Array.isArray(restaurant?.tags) ? restaurant.tags : []),
  ]
    .filter(Boolean)
    .map((value) => String(value))
    .join(' ');
};

const getRestaurantRatingCount = (restaurant) => {
  const count = Number(restaurant?.rating_count);
  if (!Number.isFinite(count)) return null;
  return Math.max(0, Math.round(count));
};

const getRestaurantRatingValue = (restaurant) => {
  const rating = Number(restaurant?.rating);
  return Number.isFinite(rating) && rating > 0 ? rating : 0;
};

const formatRestaurantRating = (restaurant) => {
  const ratingValue = getRestaurantRatingValue(restaurant);
  const ratingCount = getRestaurantRatingCount(restaurant);
  if (ratingValue <= 0) return 'Novo';
  if (ratingCount === null) return ratingValue.toFixed(1).replace('.', ',');
  if (ratingCount <= 0) return 'Novo';
  return `${ratingValue.toFixed(1).replace('.', ',')} (${ratingCount})`;
};

const Home = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { items } = useCart();
  const [categories, setCategories] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [promotedRestaurants, setPromotedRestaurants] = useState([]);
  const [topSellers, setTopSellers] = useState([]);
  const [banners, setBanners] = useState([]);
  const [bannerSettings, setBannerSettings] = useState({ interval_ms: 5000, transition: 'fade' });
  const [bannerCta, setBannerCta] = useState(DEFAULT_BANNER_CTA);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentBanner, setCurrentBanner] = useState(0);
  const [backendOnline, setBackendOnline] = useState(true);
  const [headerAddressText, setHeaderAddressText] = useState('');
  const [needsAddressRegistration, setNeedsAddressRegistration] = useState(false);
  const [userCoords, setUserCoords] = useState(null);
  const [userCity, setUserCity] = useState('');
  const categoriesScrollRef = useRef(null);
  const topSellersScrollRef = useRef(null);
  const restaurantsSectionRef = useRef(null);
  const requestCacheRef = useRef(new Map());
  const pendingRequestRef = useRef(new Map());
  const fetchSequenceRef = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const rawCategory = params.get('category');
    if (!rawCategory) {
      setSelectedCategory(null);
      return;
    }
    const category = normalizeCategoryName(rawCategory);
    setSelectedCategory(category || null);
  }, [location.search]);

  useEffect(() => {
    if (user?.role === 'admin') {
      navigate('/admin/dashboard', { replace: true });
      return;
    }
    if (user?.role === 'courier') {
      navigate('/entregas', { replace: true });
    }
  }, [user, navigate]);

  const handleProfileClick = () => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (user.role === 'admin') {
      navigate('/admin/dashboard');
      return;
    }
    if (user.role === 'merchant') {
      navigate('/logista');
      return;
    }
    if (user.role === 'courier') {
      navigate('/entregas');
      return;
    }
    navigate('/perfil');
  };


  const getPrimaryAddress = (profileData) => {
    const addresses = Array.isArray(profileData?.addresses) ? profileData.addresses : [];
    if (addresses.length > 0) {
      return addresses.find((addr) => addr?.is_primary) || addresses[0];
    }
    if (profileData?.address && typeof profileData.address === 'object') {
      return profileData.address;
    }
    return null;
  };

  const formatHeaderAddress = (address) => {
    if (!address) return '';
    const street = (address.street || '').trim();
    const number = (address.number || '').trim();
    const city = (address.city || '').trim();

    const streetLine = [street, number].filter(Boolean).join(', ');
    if (streetLine && city) return streetLine + ' - ' + city;
    if (streetLine) return streetLine;
    return city;
  };

  const parseCoord = (value) => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
      const normalized = value.trim().replace(',', '.');
      const parsed = Number(normalized);
      if (Number.isFinite(parsed)) return parsed;
    }
    return null;
  };

  const extractAddressCity = (address) => {
    if (!address || typeof address !== 'object') return '';
    const city = (
      address.city
      || address.cidade
      || address.municipality
      || address.municipio
      || ''
    );
    return String(city).trim();
  };

  const handleLocationClick = () => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (needsAddressRegistration) {
      navigate('/perfil?tab=address');
      return;
    }
    navigate('/perfil');
  };

  const handleRestaurantClick = (restaurant) => {
    if (restaurant?.is_open === false) {
      return;
    }
    navigate(`/store/${restaurant.id}`);
  };

  const handleCategoryCardClick = (categoryName) => {
    const normalizedName = normalizeCategoryName(categoryName);
    if (!normalizedName) return;
    navigate(`/restaurantes/categoria/${encodeURIComponent(normalizedName)}`);
  };

  const handleTopSellerClick = (item) => {
    const restaurant = item?.restaurant;
    if (!restaurant?.id || restaurant?.is_open === false) {
      return;
    }
    navigate(`/store/${restaurant.id}`);
  };

  const handleSearchApply = useCallback(() => {
    if (!restaurantsSectionRef.current) return;
    restaurantsSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadHeaderAddress = async () => {
      if (!user) {
        if (!isMounted) return;
        setHeaderAddressText('');
        setNeedsAddressRegistration(false);
        setUserCoords(null);
        setUserCity('');
        return;
      }

      try {
        const profileData = await backendGet('/api/auth/profile');
        if (!isMounted) return;

        const primaryAddress = getPrimaryAddress(profileData);
        const formattedAddress = formatHeaderAddress(primaryAddress);
        const lat = parseCoord(primaryAddress?.lat ?? primaryAddress?.latitude);
        const lng = parseCoord(primaryAddress?.lng ?? primaryAddress?.longitude);
        const city = extractAddressCity(primaryAddress);
        const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);
        setUserCity(city);

        if (hasCoords) {
          setUserCoords({ lat, lng });
        } else {
          setUserCoords(null);
        }

        if (formattedAddress && hasCoords) {
          setHeaderAddressText(formattedAddress);
          setNeedsAddressRegistration(false);
        } else {
          setHeaderAddressText('Cadastrar Endereço');
          setNeedsAddressRegistration(true);
        }
      } catch {
        if (!isMounted) return;
        setHeaderAddressText('Cadastrar Endereço');
        setNeedsAddressRegistration(true);
        setUserCoords(null);
        setUserCity('');
      }
    };

    loadHeaderAddress();

    return () => {
      isMounted = false;
    };
  }, [user?.id]);

  const safeBackendGetCached = useCallback(async (endpoint, options = {}, ttlMs = 20000) => {
    const cacheKey = `${options?.skipAuth ? 'public' : 'auth'}|${endpoint}`;
    const now = Date.now();
    const cached = requestCacheRef.current.get(cacheKey);
    if (cached && (now - cached.ts) <= ttlMs) {
      return cached.data;
    }

    const pending = pendingRequestRef.current.get(cacheKey);
    if (pending) {
      return pending;
    }

    const requestPromise = backendGet(endpoint, options)
      .then((data) => {
        requestCacheRef.current.set(cacheKey, { ts: Date.now(), data });
        return data;
      })
      .catch(() => null)
      .finally(() => {
        pendingRequestRef.current.delete(cacheKey);
      });

    pendingRequestRef.current.set(cacheKey, requestPromise);
    return requestPromise;
  }, []);

  useEffect(() => {
    let isMounted = true;

    const normalizeList = (payload) => {
      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload?.data)) return payload.data;
      if (Array.isArray(payload?.items)) return payload.items;
      if (Array.isArray(payload?.restaurants)) return payload.restaurants;
      if (Array.isArray(payload?.results)) return payload.results;
      return [];
    };

    const mockBanners = [
      {
        id: 1,
        title: 'Desconto de Inauguracao',
        subtitle: 'Use o cupom CORREFOOD e ganhe 20% OFF',
        image: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=1200',
        discount: '20% OFF',
        button_text: 'Aproveitar',
      },
    ];

    const fetchBanners = async () => {
      const [bannersConfigPayload, bannerPayload, bannersPayload] = await Promise.all([
        safeBackendGetCached('/api/v2/admin/public/banners', { skipAuth: true }, 60000),
        safeBackendGetCached('/api/v2/admin/public/banner', { skipAuth: true }, 60000),
        safeBackendGetCached('/api/banners', { skipAuth: true }, 60000),
      ]);

      if (!isMounted) return;

      const bannersConfigItems = normalizeList(bannersConfigPayload || []);
      const bannerSettingsData = bannersConfigPayload?.settings || {};
      const bannerCtaData = bannersConfigPayload?.cta || {};
      const bannersData = normalizeList(bannersPayload || []);

      const bannerFromAdmin = bannerPayload && bannerPayload.image_url
        ? [{
            id: 'admin-banner',
            title: bannerPayload.title || 'Correfood',
            subtitle: bannerPayload.subtitle || '',
            image: bannerPayload.image_url,
            discount: bannerPayload.badge || '',
            button_text: bannerPayload.button_text || 'Ver ofertas',
          }]
        : [];

      const resolvedBanners = bannersConfigItems.length > 0
        ? bannersConfigItems
        : (bannerFromAdmin.length > 0 ? bannerFromAdmin : (bannersData.length > 0 ? bannersData : (HOME_OFFLINE_MOCKS_ENABLED ? mockBanners : [])));

      const resolvedSettings = {
        interval_ms: Number(bannerSettingsData.interval_ms) || 5000,
        transition: bannerSettingsData.transition || 'fade',
      };
      const resolvedCta = {
        enabled: bannerCtaData?.enabled !== false,
        text: typeof bannerCtaData?.text === 'string' ? bannerCtaData.text : DEFAULT_BANNER_CTA.text,
        link: typeof bannerCtaData?.link === 'string' ? bannerCtaData.link : DEFAULT_BANNER_CTA.link,
      };

      setBanners(resolvedBanners);
      setBannerSettings(resolvedSettings);
      setBannerCta(resolvedCta);
    };

    fetchBanners();

    return () => {
      isMounted = false;
    };
  }, [safeBackendGetCached]);

  const handleBannerCtaClick = useCallback((rawLink) => {
    const link = String(rawLink || '').trim();
    if (!link || /^javascript:/i.test(link)) {
      navigate('/promocoes');
      return;
    }

    if (/^https?:\/\//i.test(link)) {
      window.location.assign(link);
      return;
    }

    if (/^[a-z0-9.-]+\.[a-z]{2,}(\/.*)?$/i.test(link)) {
      window.location.assign(`https://${link}`);
      return;
    }

    if (link.startsWith('/')) {
      navigate(link);
      return;
    }

    navigate(`/${link.replace(/^\/+/, '')}`);
  }, [navigate]);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      const currentFetchSeq = ++fetchSequenceRef.current;

      const filterCoords = Number.isFinite(userCoords?.lat) && Number.isFinite(userCoords?.lng);
      const normalizedUserCity = String(userCity || '').trim();
      const hasCityFilter = normalizedUserCity.length > 0;
      const mustRestrict = Boolean(user) && needsAddressRegistration;
      const buildRestaurantsUrl = () => {
        const params = new URLSearchParams();
        if (filterCoords) {
          params.set('lat', String(userCoords.lat));
          params.set('lng', String(userCoords.lng));
        }
        const query = params.toString();
        return `/api/restaurants/${query ? `?${query}` : ''}`;
      };
      const buildTopSellersUrl = () => {
        const params = new URLSearchParams();
        params.set('limit', '10');
        if (filterCoords) {
          params.set('lat', String(userCoords.lat));
          params.set('lng', String(userCoords.lng));
        }
        if (hasCityFilter) {
          params.set('city', normalizedUserCity);
        }
        return `/api/products/top-sellers?${params.toString()}`;
      };

      // Dados mockados caso backend esteja offline
      const mockCategories = OFFLINE_CATEGORIES;

      const mockRestaurants = [
        { id: 1, name: 'Burger House', image: 'https://images.unsplash.com/photo-1571091718767-18b5b1457add?w=500', rating: 4.8, rating_count: 128, delivery_time: '25-35 min', category: 'Hamburgueria', delivery_fee: 5.99, tags: ['Novo'], discount: '20% OFF', is_open: true },
        { id: 2, name: 'Pizza Italiana', image: 'https://images.unsplash.com/photo-1513104890138-7c749659a591?w=500', rating: 4.9, rating_count: 212, delivery_time: '30-40 min', category: 'Pizzaria', delivery_fee: 4.99, tags: ['Favorito'], discount: null, is_open: true }
      ];

      const mockBanners = [
        { id: 1, title: 'Desconto de Inauguracao', subtitle: 'Use o cupom CORREFOOD e ganhe 20% OFF', image: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=1200', discount: '20% OFF', button_text: 'Aproveitar' }
      ];

      const normalizeList = (payload) => {
        if (Array.isArray(payload)) return payload;
        if (Array.isArray(payload?.data)) return payload.data;
        if (Array.isArray(payload?.items)) return payload.items;
        if (Array.isArray(payload?.restaurants)) return payload.restaurants;
        if (Array.isArray(payload?.results)) return payload.results;
        return [];
      };

      try {
        const [categoriesPayload, restaurantsPayload, topSellersPayload] = await Promise.all([
          safeBackendGetCached('/api/categories', { skipAuth: true }, 60000),
          safeBackendGetCached(buildRestaurantsUrl(), { skipAuth: !user }, 15000),
          safeBackendGetCached(buildTopSellersUrl(), { skipAuth: !user }, 15000)
        ]);

        if (!isMounted || currentFetchSeq !== fetchSequenceRef.current) return;

        const categoriesData = normalizeCategoriesPayload(normalizeList(categoriesPayload || []));
        let restaurantsData = normalizeList(restaurantsPayload || []);
        let topSellersData = normalizeList(topSellersPayload || []);

        // Fallback: se a busca por raio nao trouxer lojas, exibe lista geral na Home.
        // A validacao final do raio continua sendo feita no checkout.
        if (filterCoords && !mustRestrict && restaurantsData.length === 0) {
          const allRestaurantsPayload = await safeBackendGetCached('/api/restaurants/', { skipAuth: !user }, 15000);
          if (!isMounted || currentFetchSeq !== fetchSequenceRef.current) return;
          restaurantsData = normalizeList(allRestaurantsPayload || []);
        }

        const promotedData = restaurantsData.filter((restaurant) => {
          const promotedFlag = restaurant?.promoted ?? restaurant?.is_promoted;
          if (typeof promotedFlag === 'boolean') return promotedFlag;
          if (typeof promotedFlag === 'string') return promotedFlag.toLowerCase() === 'true';
          return false;
        });

        const isOnline = [
          categoriesPayload,
          restaurantsPayload,
          topSellersPayload
        ].some((payload) => payload !== null);

        setCategories(categoriesData.length > 0 ? categoriesData : (HOME_OFFLINE_MOCKS_ENABLED ? mockCategories : []));
        const resolvedRestaurants = (filterCoords || mustRestrict)
          ? (filterCoords ? restaurantsData : [])
          : (restaurantsData.length > 0 ? restaurantsData : (HOME_OFFLINE_MOCKS_ENABLED ? mockRestaurants : []));
        const resolvedPromoted = (filterCoords || mustRestrict)
          ? (filterCoords ? (promotedData.length > 0 ? promotedData : restaurantsData.slice(0, 8)) : [])
          : (promotedData.length > 0 ? promotedData : (restaurantsData.length > 0 ? restaurantsData.slice(0, 8) : (HOME_OFFLINE_MOCKS_ENABLED ? mockRestaurants.slice(0, 4) : [])));

        setRestaurants(resolvedRestaurants);
        setPromotedRestaurants(resolvedPromoted);
        const locationScopedForTopSellers = filterCoords || hasCityFilter;
        const resolvedTopSellers = (locationScopedForTopSellers || mustRestrict)
          ? (locationScopedForTopSellers ? topSellersData : [])
          : topSellersData;
        setTopSellers(resolvedTopSellers);
        setBackendOnline(isOnline);

      } catch (error) {
        if (!isMounted || currentFetchSeq !== fetchSequenceRef.current) return;

        // Catch seguro: preserva estado atual para evitar regressao visual.
        setCategories((prev) => (prev?.length ? prev : (HOME_OFFLINE_MOCKS_ENABLED ? mockCategories : [])));
        if ((Number.isFinite(userCoords?.lat) && Number.isFinite(userCoords?.lng)) || hasCityFilter) {
          setRestaurants((prev) => (prev?.length ? prev : []));
          setPromotedRestaurants((prev) => (prev?.length ? prev : []));
          setTopSellers((prev) => (prev?.length ? prev : []));
        } else if (mustRestrict) {
          setRestaurants((prev) => (prev?.length ? prev : []));
          setPromotedRestaurants((prev) => (prev?.length ? prev : []));
          setTopSellers((prev) => (prev?.length ? prev : []));
        } else {
          setRestaurants((prev) => (prev?.length ? prev : (HOME_OFFLINE_MOCKS_ENABLED ? mockRestaurants : [])));
          setPromotedRestaurants((prev) => (prev?.length ? prev : (HOME_OFFLINE_MOCKS_ENABLED ? mockRestaurants.slice(0, 5) : [])));
          setTopSellers((prev) => (prev?.length ? prev : []));
        }
        setBanners((prev) => (prev?.length ? prev : (HOME_OFFLINE_MOCKS_ENABLED ? mockBanners : [])));
        setBannerSettings((prev) => (prev?.interval_ms ? prev : { interval_ms: 5000, transition: 'fade' }));
        setBackendOnline(false);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [user?.id, userCoords?.lat, userCoords?.lng, userCity, needsAddressRegistration, safeBackendGetCached]);

  useEffect(() => {
    if (banners.length === 0) return;
    setCurrentBanner((prev) => (prev >= banners.length ? 0 : prev));
  }, [banners.length]);

  useEffect(() => {
    if (banners.length === 0) return;
    const intervalMs = Math.max(2000, Number(bannerSettings.interval_ms) || 5000);
    const bannerInterval = setInterval(() => {
      setCurrentBanner((prev) => (prev + 1) % banners.length);
    }, intervalMs);
    return () => clearInterval(bannerInterval);
  }, [banners.length, bannerSettings.interval_ms]);

  const normalizedSearchTerm = normalizeSearchToken(searchTerm);
  const matchesRestaurantSearch = useCallback((restaurant) => {
    if (!normalizedSearchTerm) return true;
    const searchableText = normalizeSearchToken(buildRestaurantSearchText(restaurant));
    return searchableText.includes(normalizedSearchTerm);
  }, [normalizedSearchTerm]);

  const filteredRestaurants = useMemo(() => {
    return restaurants.filter((restaurant) => {
      const matchesSearch = matchesRestaurantSearch(restaurant);
      const matchesCategory = !selectedCategory || matchRestaurantCategory(restaurant?.categories || restaurant?.category, selectedCategory);
      return matchesSearch && matchesCategory;
    });
  }, [restaurants, selectedCategory, matchesRestaurantSearch]);

  const filteredPromotedRestaurants = useMemo(() => {
    return promotedRestaurants.filter((restaurant) => {
      const matchesSearch = matchesRestaurantSearch(restaurant);
      const matchesCategory = !selectedCategory || matchRestaurantCategory(restaurant?.categories || restaurant?.category, selectedCategory);
      return matchesSearch && matchesCategory;
    });
  }, [promotedRestaurants, selectedCategory, matchesRestaurantSearch]);

  const visibleCategories = useMemo(() => {
    if (!Array.isArray(categories) || categories.length === 0) return [];
    if (!Array.isArray(restaurants) || restaurants.length === 0) return [];

    return categories.filter((category) => {
      const categoryName = normalizeCategoryName(category?.name);
      return restaurants.some((restaurant) => matchRestaurantCategory(restaurant?.categories || restaurant?.category, categoryName));
    });
  }, [categories, restaurants]);

  const formatPrice = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      return 'R$ 0,00';
    }
    return `R$ ${parsed.toFixed(2).replace('.', ',')}`;
  };

  const Header = () => (
    <header className="correFood-header">
      <div className="container">
        <div className="header-top">
          {!user ? (
            <button
              type="button"
              className="home-login-entry"
              onClick={handleLocationClick}
              aria-label="Entrar na conta"
            >
              <span className="home-login-entry-icon" aria-hidden="true">
                <User size={16} />
              </span>
              <span className="home-login-entry-text">Entrar na conta</span>
              <ChevronRight size={16} aria-hidden="true" />
            </button>
          ) : (
            <div
              className="location"
              onClick={handleLocationClick}
              style={{ cursor: 'pointer' }}
            >
              <MapPin size={18} className="location-icon" />
              <span>{headerAddressText}</span>
              <ChevronRight size={16} />
            </div>
          )}
<div className="header-actions">
  {user ? (    // Usuario logado
    <div className="session-actions">
      <button
        className="btn-icon"
        onClick={handleProfileClick}
        title={user.email}
      >
        <User size={20} />
      </button>

      <button
        onClick={signOut}
        className="btn-logout"
      >
        Sair
      </button>
    </div>
  ) : null}

  <button
    className="btn-icon mobile-menu-btn"
    onClick={() => setIsMenuOpen(!isMenuOpen)}
  >
    {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
  </button>
</div>
        </div>
        
        <div className="header-main">
          <div className="brand">
            <h1 className="logo">CorreFood</h1>
            <p className="tagline">Entrega rápida e saborosa</p>
          </div>
          
          <nav className={`main-nav ${isMenuOpen ? 'open' : ''}`}>
            <a href="#home" className="nav-link active">Início</a>
            <a
              href="/restaurantes"
              className="nav-link"
              onClick={(event) => {
                event.preventDefault();
                navigate('/restaurantes');
              }}
            >
              Restaurantes
            </a>
            <a
              href="/promocoes"
              className="nav-link"
              onClick={(event) => {
                event.preventDefault();
                navigate('/promocoes');
              }}
            >
              Promoções
            </a>
            <a
              href="/ajuda"
              className="nav-link"
              onClick={(event) => {
                event.preventDefault();
                navigate('/ajuda');
              }}
            >
              Ajuda
            </a>
          </nav>
          
          <button
            className="cart-btn"
            onClick={() => navigate('/checkout')}
            title="Abrir carrinho"
          >
            <ShoppingBag size={20} />
            <span className="cart-count">
              {Array.isArray(items) ? items.reduce((sum, item) => sum + (item.quantity || 0), 0) : 0}
            </span>
          </button>
        </div>

        <div className="search-container">
          <div className="search-box">
            <Search size={20} className="search-icon" />
            <input
              type="text"
              placeholder="Buscar por restaurantes, pratos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  handleSearchApply();
                }
              }}
            />
            <button className="filter-btn" type="button" onClick={handleSearchApply} aria-label="Aplicar busca">
              <Filter size={18} />
            </button>
          </div>
        </div>

        <div className="home-quick-pills">
          <span className="home-pill">Entrega media de 20 a 40 min</span>
          <span className="home-pill">Cupons e ofertas todos os dias</span>
          <span className="home-pill">Pagamento seguro e rastreado</span>
        </div>
      </div>
    </header>
  );

  const BannerCarousel = () => (
    <section className="banner-section">
      <div className={`banner-carousel transition-${bannerSettings.transition || 'fade'}`}>
        {banners.map((banner, index) => {
          const badge = String(banner.badge || banner.discount || '').trim();
          const title = String(banner.title || '').trim();
          const subtitle = String(banner.subtitle || '').trim();
          const ctaEnabled = bannerCta?.enabled !== false;
          const ctaText = String(bannerCta?.text || '').trim()
            || String(banner.button_text || '').trim()
            || 'Ver ofertas';
          const ctaLink = String(bannerCta?.link || '').trim() || '/promocoes';
          const isCtaOnly = !badge && !title && !subtitle;

          return (
            <div
              key={`${banner.id || "banner"}-${index}`}
              className={`banner-slide ${index === currentBanner ? 'active' : ''}`}
              style={{ backgroundImage: `url(${banner.image_url || banner.image})` }}
            >
              <div className="banner-overlay">
                <div className="container">
                  <div className={`banner-content ${isCtaOnly ? 'banner-content-cta-only' : ''}`}>
                    {badge ? (
                      <span className="banner-discount">{badge}</span>
                    ) : null}
                    {title ? <h2 className="banner-title">{title}</h2> : null}
                    {subtitle ? <p className="banner-subtitle">{subtitle}</p> : null}
                    {ctaEnabled ? (
                      <button
                        type="button"
                        className="btn-primary banner-btn"
                        onClick={() => handleBannerCtaClick(ctaLink)}
                      >
                        {ctaText}
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        <div className="banner-dots">
          {banners.map((_, index) => (
            <button
              key={index}
              className={`banner-dot ${index === currentBanner ? 'active' : ''}`}
              onClick={() => setCurrentBanner(index)}
            />
          ))}
        </div>
      </div>
    </section>
  );

  const CategoryGrid = () => (
    <section className="categories-section">
      <div className="container">
        <div className="section-shell">
          <div className="home-section-header">
            <span className="section-kicker">Explorar</span>
            <h2 className="section-title">O que voce esta procurando?</h2>
            <p className="section-subtitle">Escolha uma categoria e encontre opcoes relevantes mais rapido.</p>
          </div>
          {visibleCategories.length > 0 ? (
            <div className="categories-carousel-wrapper">
            <button
              type="button"
              className="categories-nav-btn left"
              aria-label="Categorias anteriores"
              onClick={() => {
                if (!categoriesScrollRef.current) return;
                categoriesScrollRef.current.scrollBy({ left: -360, behavior: 'smooth' });
              }}
            >
              <ChevronLeft size={18} />
            </button>

            <div className="categories-grid" ref={categoriesScrollRef}>
              {visibleCategories.map((category) => {
                const categoryName = normalizeCategoryName(category.name);
                return (
                  <div
                    key={category.id}
                    className={`category-card ${selectedCategory === categoryName ? 'selected' : ''}`}
                    onClick={() => handleCategoryCardClick(categoryName)}
                  >
                    <div className="category-image">
                      <img
                        src={category.image_url || category.image}
                        alt={categoryName}
                        loading="lazy"
                        decoding="async"
                      />
                      {category.icon ? (
                        <div className="category-overlay">
                          <span className="category-icon">{category.icon}</span>
                        </div>
                      ) : null}
                    </div>
                    <span className="category-name">{categoryName}</span>
                  </div>
                );
              })}
            </div>

            <button
              type="button"
              className="categories-nav-btn right"
              aria-label="Proximas categorias"
              onClick={() => {
                if (!categoriesScrollRef.current) return;
                categoriesScrollRef.current.scrollBy({ left: 360, behavior: 'smooth' });
              }}
            >
              <ChevronRight size={18} />
            </button>
            </div>
          ) : (
            <div className="empty-state">
              <p>Nenhuma categoria disponivel no momento.</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );

  const PromotedRestaurants = () => (
    <section className="promoted-section">
      <div className="container">
        <div className="section-shell">
          <div className="section-header">
            <div>
              <span className="section-kicker">Destaques</span>
              <h2 className="section-title">Restaurantes em alta</h2>
              <p className="section-subtitle">Lojas com maior procura e melhor avaliacao no momento.</p>
            </div>
            <button className="btn-link" onClick={() => navigate('/restaurantes')}>Ver todos</button>
          </div>
          {filteredPromotedRestaurants.length === 0 ? (
            <div className="empty-state">
              <p>
                {normalizedSearchTerm
                  ? 'Nenhuma loja em destaque encontrada para essa busca.'
                  : 'Nenhuma loja em destaque disponivel no momento.'}
              </p>
            </div>
          ) : (
            <div className="restaurants-carousel">
          {filteredPromotedRestaurants.map((restaurant) => (
            <div key={restaurant.id} className={`restaurant-card featured-restaurant-card ${restaurant.is_open === false ? "closed" : ""}`}
            onClick={() => handleRestaurantClick(restaurant)}
            style={{ cursor: restaurant.is_open === false ? 'not-allowed' : 'pointer' }} 
            >
              <div className="restaurant-image">
                  <img
                    src={resolveStoreCardImage(restaurant)}
                    alt={restaurant.name || "Restaurante"}
                    loading="lazy"
                    decoding="async"
                    onError={(e) => { e.target.onerror = null; e.target.src = DEFAULT_BANNER; }}
                  />
                {restaurant.discount && (
                  <div className="discount-badge">{restaurant.discount}</div>
                )}
                {restaurant.is_open === false && (
                  <div className="closed-badge">Fechado</div>
                )}
              </div>
              <div className="restaurant-info">
                <h3 className="restaurant-name">{restaurant.name}</h3>
                <div className="restaurant-meta">
                  <div className="rating">
                    <Star size={14} fill="currentColor" />
                    <span>{formatRestaurantRating(restaurant)}</span>
                  </div>
                  <div className="delivery-time">
                    <Clock size={14} />
                    <span>{restaurant.delivery_time || '20-30 min'}</span>
                  </div>
                </div>
                <div className="restaurant-tags">
                  {restaurant.tags?.slice(0, 2).map((tag, index) => (
                    <span key={index} className="tag">{tag}</span>
                  ))}
                </div>
                <div className="restaurant-card-footer">
                  <div className="delivery-fee">
                    Entrega: R$ {(restaurant.delivery_fee || 0).toFixed(2).replace('.', ',')}
                  </div>
                  <span className="restaurant-cta">
                    {restaurant.is_open === false ? 'Indisponivel' : 'Ver cardapio'}
                  </span>
                </div>
              </div>
            </div>
          ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );

  const TopSellersSection = () => {
    if (!Array.isArray(topSellers) || topSellers.length === 0) {
      return null;
    }

    return (
      <section className="top-sellers-section">
        <div className="container">
          <div className="section-shell">
            <div className="section-header">
              <div>
                <span className="section-kicker">Ranking</span>
                <h2 className="section-title">Top 10 produtos mais vendidos</h2>
                <p className="section-subtitle">Produtos lideres de venda para facilitar sua escolha.</p>
              </div>
            </div>

            <div className="top-sellers-carousel-wrapper">
            <button
              type="button"
              className="top-sellers-nav-btn left"
              aria-label="Produtos anteriores"
              onClick={() => {
                if (!topSellersScrollRef.current) return;
                topSellersScrollRef.current.scrollBy({ left: -280, behavior: 'smooth' });
              }}
            >
              <ChevronLeft size={18} />
            </button>

            <div className="top-sellers-carousel" ref={topSellersScrollRef}>
              {topSellers.map((item, index) => {
                const product = item?.product || {};
                const restaurant = item?.restaurant || {};
                const soldQuantity = Number(item?.sold_quantity || 0);
                const finalPrice = product?.price_promotional ?? product?.preco ?? 0;
                const productName = product?.name || product?.nome || 'Produto';
                const storeName = restaurant?.name || 'Loja';
                const cardImage =
                  product?.image_url ||
                  product?.imagem ||
                  restaurant?.logo_url ||
                  restaurant?.banner_url ||
                  DEFAULT_BANNER;
                const isStoreOpen = restaurant?.is_open !== false;

                return (
                  <div
                    key={`${product?.id || 'product'}-${restaurant?.id || 'store'}-${index}`}
                    className={`top-seller-card ${isStoreOpen ? '' : 'closed'}`}
                    onClick={() => handleTopSellerClick(item)}
                    style={{ cursor: isStoreOpen ? 'pointer' : 'not-allowed' }}
                  >
                    <div className="top-seller-image">
                      <img
                        src={cardImage}
                        alt={productName}
                        loading="lazy"
                        decoding="async"
                        onError={(e) => { e.target.onerror = null; e.target.src = DEFAULT_BANNER; }}
                      />
                      <span className="top-seller-rank">#{item?.rank || index + 1}</span>
                      {!isStoreOpen && (
                        <div className="closed-badge">Fechado</div>
                      )}
                    </div>

                    <div className="top-seller-body">
                      <h3 className="top-seller-name">{productName}</h3>
                      <p className="top-seller-store">{storeName}</p>
                      <div className="top-seller-meta">
                        <span>{soldQuantity} vendidos</span>
                        <span>{formatPrice(finalPrice)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <button
              type="button"
              className="top-sellers-nav-btn right"
              aria-label="Próximos produtos"
              onClick={() => {
                if (!topSellersScrollRef.current) return;
                topSellersScrollRef.current.scrollBy({ left: 280, behavior: 'smooth' });
              }}
            >
              <ChevronRight size={18} />
            </button>
            </div>
          </div>
        </div>
      </section>
    );
  };

  const RestaurantsList = () => (
    <section className="restaurants-section" ref={restaurantsSectionRef}>
      <div className="container">
        <div className="section-shell">
          <div className="section-header">
            <div>
              <span className="section-kicker">Descobrir</span>
              <h2 className="section-title">
                {selectedCategory ? `${selectedCategory} proximos` : 'Todos os restaurantes'}
              </h2>
              <p className="section-subtitle">
                Veja as melhores opcoes da sua regiao e acompanhe tempo medio e taxa de entrega.
              </p>
            </div>
            {selectedCategory && (
              <button className="btn-link" onClick={() => setSelectedCategory(null)}>
                Limpar filtro
              </button>
            )}
          </div>
          <div className="restaurants-grid">
            {filteredRestaurants.map((restaurant) => (
              <div key={restaurant.id} className={`restaurant-card list-restaurant-card ${restaurant.is_open === false ? "closed" : ""}`}
              onClick={() => handleRestaurantClick(restaurant)}
              style={{ cursor: restaurant.is_open === false ? 'not-allowed' : 'pointer' }}
              >
                <div className="restaurant-image">
                  <img
                    src={resolveStoreCardImage(restaurant)}
                    alt={restaurant.name || "Restaurante"}
                    loading="lazy"
                    decoding="async"
                    onError={(e) => { e.target.onerror = null; e.target.src = DEFAULT_BANNER; }}
                  />
                  {restaurant.discount && (
                    <div className="discount-badge">{restaurant.discount}</div>
                  )}
                  {restaurant.is_open === false && (
                    <div className="closed-badge">Fechado</div>
                  )}
                </div>
                <div className="restaurant-info">
                  <h3 className="restaurant-name">{restaurant.name}</h3>
                  <div className="restaurant-meta">
                    <div className="rating">
                      <Star size={14} fill="currentColor" />
                      <span>{formatRestaurantRating(restaurant)}</span>
                    </div>
                    <div className="delivery-time">
                      <Clock size={14} />
                      <span>{restaurant.delivery_time || '20-30 min'}</span>
                    </div>
                  </div>
                  <div className="restaurant-tags">
                    {restaurant.tags?.slice(0, 3).map((tag, index) => (
                      <span key={index} className="tag">{tag}</span>
                    ))}
                  </div>
                  <div className="restaurant-card-footer">
                    <div className="delivery-fee">
                      Entrega: R$ {(restaurant.delivery_fee || 0).toFixed(2).replace('.', ',')}
                    </div>
                    <span className="restaurant-cta">
                      {restaurant.is_open === false ? 'Fechada' : 'Abrir loja'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredRestaurants.length === 0 && (
            <div className="empty-state">
              <p>Nenhum restaurante encontrado.</p>
              <button className="btn-primary" onClick={() => {setSearchTerm(''); setSelectedCategory(null);}}>
                Ver todos os restaurantes
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
  const Footer = () => (
    <footer className="correFood-footer">
      <div className="container">
        <div className="footer-content">
          <div className="footer-brand">
            <h3 className="logo">CorreFood</h3>
            <p>A melhor experiência em delivery de comida do Brasil.</p>
          </div>
          <div className="footer-links">
            <div className="footer-column">
              <h4>Empresa</h4>
              <ul>
                <li><a href="/sobre-nos">Sobre nós</a></li>
                <li><a href="/legal#politica-privacidade" translate="no" className="notranslate">Política de Privacidade</a></li>
                <li><a href="/legal#termos-condicoes" translate="no" className="notranslate">Termos de Condições</a></li>
              </ul>
            </div>
            <div className="footer-column">
              <h4>Ajuda</h4>
              <ul>
                <li><a href="/ajuda">Perguntas frequentes</a></li>
                <li><a href="/ajuda">Contato</a></li>
                <li><a href="/ajuda">Suporte</a></li>
              </ul>
            </div>
            <div className="footer-column">
              <h4>Parceiros</h4>
              <ul>
                <li><a href="/register?role=merchant">Seja um parceiro</a></li>
                <li><a href="/register?role=courier">Seja entregador</a></li>
              </ul>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2024 CorreFood. Todos os direitos reservados.</p>
        </div>
      </div>
    </footer>
  );

  return (
    <div className="correFood-app home-v2">
      {Header()}
      <main className="home-main">
        {!backendOnline && (
          <div className="container home-alert-wrap">
            <div className="home-offline-alert">
              Backend local offline. Dados mock da Home estao desativados para facilitar diagnostico.
            </div>
          </div>
        )}
        <BannerCarousel />
        <CategoryGrid />
        <TopSellersSection />
        <PromotedRestaurants />
        <RestaurantsList />
      </main>
      <Footer />
    </div>
  );
};

export default Home;



























